import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import { App, Page } from 'Components';
import { Config } from '@forgerock/javascript-sdk';
import { createBrowserRouter, isRouteErrorResponse, Navigate, RouterProvider, useRouteError } from 'react-router-dom';
import { AppContextProvider, JourneyContextProvider } from 'Contexts';
import { Configs, Journey } from 'Constants';
import { worker } from '@mocks';
import * as process from 'process';

const { clientId, url, realmPath, redirectUri } = Configs.forgeRock;

Config.set({
  clientId,
  redirectUri,
  scope: 'openid profile email',
  serverConfig: {
    baseUrl: `${url}/am`,
    timeout: 15000,
  },
  realmPath,
});

const RouterError = () => {
  const error = useRouteError();
  let errorMessage;

  if (isRouteErrorResponse(error)) {
    // error is type `ErrorResponse`
    errorMessage = error.data?.message || error.statusText;
  } else if (error instanceof Error) {
    errorMessage = error.message;
  } else if (typeof error === 'string') {
    errorMessage = error;
  } else {
    errorMessage = 'Unknown error';
  }

  return <div>{errorMessage}</div>;
};

const currentUrl = new URL(window.location.href);
const socialConnect = {
  socialConnectCode: currentUrl.searchParams.get('code') || undefined,
  socialConnectState: currentUrl.searchParams.get('state') || undefined,
};

const router = createBrowserRouter(
  [
    {
      path: '/',
      element: (
        <AppContextProvider>
          <App />
        </AppContextProvider>
      ),
      errorElement: <RouterError />,
      children: [
        {
          path: '',
          element: <Navigate to="login" replace />,
        },
        {
          path: 'login',
          element: (
            <JourneyContextProvider journey={Journey.LOGIN} key="login" socialConnect={socialConnect}>
              <Page />
            </JourneyContextProvider>
          ),
        },
        {
          path: 'reset-password',
          element: (
            <JourneyContextProvider journey={Journey.RESET_PASSWORD} key="reset-password">
              <Page />
            </JourneyContextProvider>
          ),
        },
        {
          path: 'callback',
          element: <JourneyContextProvider journey={Journey.LOGIN} key="callback" />,
        },
      ],
    },
  ],
  { basename: '/authentification' },
);

const prepare = async () => {
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_MOCK_API === 'true') {
    localStorage.setItem(
      'goTo',
      `${process.env.REACT_APP_E2E_IDENTITY_PROVIDER_URL}/am/oauth2/alpha/authorize?scope=openid%20email%20profile&acr_values=darty_com%20${
        process.env.REACT_APP_PIPE === 'true' ? 'pipe' : 'care'
      }${process.env.REACT_APP_WITH_BREADCRUMB === 'true' ? '%20with_breadcrumb' : ''}${
        process.env.REACT_APP_ELIGIBLE_TO_SHORTCUT_PAYPAL === 'true' ? '%20eligible_to_shortcut_paypal' : ''
      }&response_type=id_token&redirect_uri=https://ecom.darty.com:8443/espace_client/identity/complete-login&state=some_state&nonce=some_nonce&client_id=Darty&response_mode=form_post`,
    );
    await worker.start({
      serviceWorker: {
        url: `${process.env.PUBLIC_URL}/mockServiceWorker.js`,
      },
      onUnhandledRequest: 'bypass',
    });
  }
};

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
prepare().then(() => root.render(<RouterProvider router={router} />));
